import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { PLANNER_DARK } from '../licensing/licenceApi';

/**
 * The plan picker: "my plans" (owned) and "shared with me" (invited via
 * memberEmails), both newest-first using the composite indexes. "New plan" mints
 * a plan when the user is entitled, or shows the upsell when they aren't — a free
 * author can't start their own plan but can still open ones shared with them.
 *
 * Presentational + callback-driven for testability:
 *   authUser   — Firebase user (uid + email) or null
 *   entitled   — may the user create their own plan? (planner/bundle licence or
 *                paid author) — decides New-plan vs upsell
 *   onOpen(id) — open a plan · onNewPlan() — create (server decides who may) ·
 *   onSignIn() — begin sign-in (the collab model is auth-based)
 * `entitled` gates only the (post-launch) "subscribe" note; New plan always asks
 * the createPlan callable, which authorises superadmin/entitled server-side.
 */
const toMillis = (v) => {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v.seconds) return v.seconds * 1000;
  return 0;
};
const fmtDate = (v) => {
  const ms = toMillis(v);
  if (!ms) return '';
  try { return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

function PlanList({ plans, emptyText, onOpen }) {
  if (!plans.length) return <p className="picker-empty">{emptyText}</p>;
  return (
    <ul className="picker-list">
      {plans.map((p) => (
        <li key={p.id}>
          <button type="button" className="picker-plan" onClick={() => onOpen(p.id)}>
            <span className="picker-plan-title">{(p.title || '').trim() || 'Untitled plan'}</span>
            {p.updatedAt && <span className="picker-plan-meta">Edited {fmtDate(p.updatedAt)}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function PlanPicker({ authUser, entitled, onOpen, onNewPlan, onSignIn }) {
  const [mine, setMine] = useState([]);
  const [shared, setShared] = useState([]);

  const uid = authUser && authUser.uid;
  const email = (authUser && authUser.email ? authUser.email : '').toLowerCase();

  useEffect(() => {
    if (!uid) { setMine([]); return undefined; }
    const q = query(collection(db, 'plans'), where('ownerUid', '==', uid), orderBy('updatedAt', 'desc'));
    return onSnapshot(q, (snap) => setMine(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setMine([]));
  }, [uid]);

  useEffect(() => {
    if (!email) { setShared([]); return undefined; }
    const q = query(collection(db, 'plans'), where('memberEmails', 'array-contains', email), orderBy('updatedAt', 'desc'));
    return onSnapshot(q, (snap) => setShared(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setShared([]));
  }, [email]);

  if (!authUser) {
    return (
      <div className="plot-planner picker">
        <div className="picker-head">
          <h1>Your plans</h1>
          <p className="picker-lede">Sign in to see your plans and any a co-writer has shared with you.</p>
          <button type="button" className="btn solid" onClick={onSignIn}>Sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="plot-planner picker">
      <div className="picker-head">
        <div className="picker-head-row">
          <h1>Your plans</h1>
          <button
            type="button"
            className="btn solid picker-new"
            onClick={onNewPlan}
          >
            + New plan
          </button>
        </div>
        {/* Server decides who may create (superadmin / entitled): New plan always
            asks the callable. The "subscribe" note only makes sense once the paid
            planner is live — hidden while dark. */}
        {!entitled && !PLANNER_DARK && (
          <p className="picker-upsell-note">
            Subscribe to start your own plan. You can still open plans shared with you below.
          </p>
        )}
      </div>

      <section className="picker-section">
        <h2>My plans</h2>
        <PlanList plans={mine} emptyText="You haven't created a plan yet." onOpen={onOpen} />
      </section>

      <section className="picker-section">
        <h2>Shared with me</h2>
        <PlanList plans={shared} emptyText="No one has shared a plan with you yet." onOpen={onOpen} />
      </section>
    </div>
  );
}
